"use client";

import { useOrganizations } from "@/lib/hooks/use-organizations";
import { ChevronsUpDown, Building2 } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export function OrgSwitcher() {
  const { organizations, currentOrgId, switchOrg, isLoading } = useOrganizations();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentOrg = organizations.find((o) => o.id === currentOrgId);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSwitch = async (orgId: string) => {
    if (orgId === currentOrgId) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await switchOrg(orgId);
      setOpen(false);
    } finally {
      setSwitching(false);
    }
  };

  if (isLoading || organizations.length === 0) {
    return (
      <div className="text-xs text-muted-foreground px-2 py-1">
        Loading...
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={switching}
        className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-accent transition-colors disabled:opacity-50"
      >
        <Building2 className="size-3.5 text-muted-foreground" />
        <span className="max-w-[120px] truncate">{currentOrg?.name ?? "Select org"}</span>
        <ChevronsUpDown className="size-3 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] rounded-md border bg-popover p-1 shadow-md">
          <div className="px-2 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Organizations
          </div>
          {organizations.map((org) => (
            <button
              key={org.id}
              onClick={() => handleSwitch(org.id)}
              disabled={switching}
              className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs transition-colors hover:bg-accent ${
                org.id === currentOrgId ? "bg-accent font-medium" : ""
              }`}
            >
              <Building2 className="size-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">{org.name}</span>
              {org.id === currentOrgId && (
                <span className="ml-auto text-[10px] text-muted-foreground">Current</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
